class QuantityInput extends HTMLElement {
  connectedCallback() {
    this.input = this.querySelector('input[type="number"]');
    this.maxQuantity = Number.parseInt(this.input.getAttribute('max'));
    this.minQuantity = Number.parseInt(this.input.getAttribute('min'));
    this.plusButton = this.querySelector('button[name="plus"]');
    this.minusButton = this.querySelector('button[name="minus"]');

    this.bindEventListeners();
    this.syncHostDisabledAttribute();
  }

  /**
   * Mirror the input's disabled state on the host so &[disabled] host styles apply.
   */
  syncHostDisabledAttribute() {
    if (this.input.hasAttribute('disabled')) {
      this.setAttribute('disabled', '');
    } else {
      this.removeAttribute('disabled');
    }
  }

  bindEventListeners() {
    this.input.addEventListener(
      'change',
      this.dispatchQuantityChangeEvent.bind(this)
    );
    this.plusButton.addEventListener(
      'click',
      this.incrementAndDispatch.bind(this)
    );
    this.minusButton.addEventListener(
      'click',
      this.decrementAndDispatch.bind(this)
    );

    // update the input value when the quantity changes but make sure the user does not attempt to enter a value greater than the max
    this.input.addEventListener('pxl:changeQuantity', event => {
      const { detail } = event;
      detail.value > this.maxQuantity
        ? this.setValue(this.maxQuantity)
        : this.setValue(detail.value);
    });
  }

  /**
   * Increment the input value by the step value
   * @param {event} event
   * @return {void}
   */
  incrementAndDispatch(event) {
    event.preventDefault();
    this.incrementValue();
    this.disableAndEnableButtons();
    this.dispatchQuantityChangeEvent();
  }

  /**
   * Decrement the input value by the step value
   * @param {object} event
   * @return {void}
   */
  decrementAndDispatch(event) {
    event.preventDefault();
    this.decrementValue();
    this.disableAndEnableButtons();
    this.dispatchQuantityChangeEvent();
  }

  /**
   * Increment the input value by the step value
   * @return {void}
   */
  incrementValue() {
    this.input.value =
      Number.parseInt(this.input.value, 10) +
      Number.parseInt(this.input.step, 10);
  }

  /**
   * Decrement the input value by the step value, or the min value if the step value is greater than the current value
   * @return {void}
   */
  decrementValue() {
    this.input.value = Math.max(
      Number.parseInt(this.input.value, 10) -
        Number.parseInt(this.input.step, 10),
      this.minQuantity
    );
  }

  /**
   * Sets the value to the passed value, and re-enables/disables buttons based on the new value
   * @param {Number} value - the new value to set the input to
   */
  setValue(value) {
    this.input.value = value;
    this.disableAndEnableButtons();
  }

  /**
   * Dispatch the "pxl:changeQuantity" event
   */
  dispatchQuantityChangeEvent() {
    this.input.dispatchEvent(
      new CustomEvent('pxl:changeQuantity', {
        detail: {
          value: this.input.value
        },
        bubbles: true
      })
    );
  }

  /**
   * Disable and enable buttons based on min and max quantity
   */
  disableAndEnableButtons() {
    if (this.input.value <= this.minQuantity) {
      this.minusButton.setAttribute('disabled', true);
    } else {
      this.minusButton.removeAttribute('disabled');
    }
    if (this.input.value >= this.maxQuantity) {
      this.plusButton.setAttribute('disabled', true);
    } else {
      this.plusButton.removeAttribute('disabled');
    }
  }

  /**
   * Enable the input and buttons
   * @returns {void}
   */
  enable() {
    this.input.removeAttribute('disabled');
    this.plusButton.removeAttribute('disabled');
    this.minusButton.removeAttribute('disabled');
    this.syncHostDisabledAttribute();
  }

  /**
   * Disable the input and buttons
   * @returns {void}
   */
  disable() {
    this.input.setAttribute('disabled', '');
    this.plusButton.setAttribute('disabled', '');
    this.minusButton.setAttribute('disabled', '');
    this.syncHostDisabledAttribute();
  }

  /**
   * Set the loading state of the input. This is used when the quantity is being updated
   */
  setLoadingState() {
    this.input.setAttribute('disabled', true);
    this.plusButton.setAttribute('disabled', true);
    this.minusButton.setAttribute('disabled', true);
    // eslint-disable-next-line wc/no-self-class
    this.classList.add('loading');
  }

  /**
   * Remove the loading state of the input. This is used when the quantity is done updating
   */
  removeLoadingState() {
    this.input.removeAttribute('disabled');
    this.plusButton.removeAttribute('disabled');
    this.minusButton.removeAttribute('disabled');
    // eslint-disable-next-line wc/no-self-class
    this.classList.remove('loading');
  }
}

customElements.get('quantity-input') ||
  customElements.define('quantity-input', QuantityInput);
